import type { CaptureFileJSONObject } from '@ikegps/core-api';

import { IkeId } from '../../../id';
import { CollectionModel } from '../collection.data';
import { CollectionModelHiddenImageVisibility } from './hidden.imageVisibility';
import type {
	CollectionModelImageVisibility,
	ModelStateImageVisibility,
	ModelStateImageVisibilityTag,
} from './imageVisibility';

export type ImageVisibilityId = string;
export type ModelFieldStateImageVisibility = ImageVisibilityId;

function makeImageVisibilityState(tag: ModelStateImageVisibilityTag, id: string): ModelStateImageVisibility {
	switch (tag) {
		case 'hidden':
			return CollectionModelHiddenImageVisibility.state(id);
		default:
			throw new Error(`Unknown marker type: "${tag}"`);
	}
}

export class CollectionModelImageVisibilityStateMutator {
	state: CollectionModelImageVisibilityState;
	constructor(state: CollectionModelImageVisibilityState) {
		this.state = state;
	}

	getState(): ModelFieldStateImageVisibility {
		const collectionState = this.state.collection.state;

		const captureImageVisibilityKey = CollectionModelImageVisibilityState.captureImageVisibilityId(
			this.state.captureId
		);
		return collectionState.getFieldValue(captureImageVisibilityKey) as ModelFieldStateImageVisibility;
	}

	setState(state: ModelFieldStateImageVisibility) {
		const collectionState = this.state.collection.state;
		const captureImageVisibilityKey = CollectionModelImageVisibilityState.captureImageVisibilityId(
			this.state.captureId
		);
		collectionState.setFieldValue(captureImageVisibilityKey, state);
	}

	create(tag: ModelStateImageVisibilityTag, id = IkeId.generate()): ImageVisibilityId {
		const state = makeImageVisibilityState(tag, id);
		return this.createWithState(state);
	}

	clear() {
		this.state = null;
	}

	private createWithState(state: ModelStateImageVisibility): ImageVisibilityId {
		const collectionState = this.state.collection.state;
		const captureId = this.state.captureId;

		const stateId = CollectionModelImageVisibilityState.imageVisibilityId(captureId);
		if (collectionState.fields.get(stateId) != null) {
			throw new Error(`Image Visibility "${stateId}" already exists`);
		}

		collectionState.setFieldValue(stateId, state);
		this.setState(state.id);

		return state.id;
	}
}

const ID_PREFIX_IMAGE_VISIBILITY = 'iv';
const ID_PREFIX_CAPTURE_IMAGE_VISIBILITY = 'civ';

export class CollectionModelImageVisibilityState {
	static imageVisibilityId(captureId: string): string {
		return `${ID_PREFIX_IMAGE_VISIBILITY}:${captureId}:image-visibility-hidden`;
	}

	static captureImageVisibilityId(captureId: string): string {
		return `${ID_PREFIX_CAPTURE_IMAGE_VISIBILITY}:${captureId}`;
	}

	static getCaptureImageVisibilityId(key: string): string | null {
		if (key.startsWith(`${ID_PREFIX_CAPTURE_IMAGE_VISIBILITY}:`)) {
			return key.substr(3);
		}

		return null;
	}
	Mutator: CollectionModelImageVisibilityStateMutator = new CollectionModelImageVisibilityStateMutator(this);
	id = IkeId.generate();
	collection: CollectionModel;
	captureId: string;

	imageVisibility: CollectionModelImageVisibility;

	constructor(collection: CollectionModel, captureId: string) {
		this.collection = collection;
		this.captureId = captureId;
	}

	hasImageVisibility() {
		return !!this.state;
	}

	hideForExport() {
		if (!this.hasImageVisibility()) {
			return false;
		} else {
			let visibility = this.getImageVisibility();
			switch (visibility.state.tag) {
				case 'hidden':
					const hidden = this.getImageVisibility<CollectionModelHiddenImageVisibility>().hidden;
					return Boolean(hidden);
				default:
					return false;
			}
		}
	}

	getImageVisibility<T extends CollectionModelImageVisibility = CollectionModelImageVisibility>(): T {
		let imageVisibility = this.imageVisibility as CollectionModelImageVisibility;
		if (imageVisibility == null) {
			const imageVisibilityId = CollectionModelImageVisibilityState.imageVisibilityId(this.captureId);
			let imageVisibilityState = this.collection.state.getFieldValue(imageVisibilityId) as ModelStateImageVisibility;
			if (imageVisibilityState == null) {
				//if we can't find a state with the current id, we recreate a new state with the same id, and query it again
				this.Mutator.create('hidden');
				imageVisibilityState = this.collection.state.getFieldValue(imageVisibilityId) as ModelStateImageVisibility;
			}

			switch (imageVisibilityState.tag) {
				case 'hidden':
					imageVisibility = new CollectionModelHiddenImageVisibility(this, imageVisibilityId, this.captureId);
					break;
				default:
					throw new Error(
						`Capture ${this.captureId}:Unknown visibility correction type: "${imageVisibilityState.tag}"`
					);
			}

			this.imageVisibility = imageVisibility;
		}

		return imageVisibility as T;
	}

	value(): CollectionModelImageVisibility {
		return this.getImageVisibility();
	}

	get capture(): CaptureFileJSONObject {
		return this.collection.state.getCapture(this.captureId);
	}

	private get state(): ModelFieldStateImageVisibility {
		return this.collection.state.getFieldValue(
			CollectionModelImageVisibilityState.captureImageVisibilityId(this.captureId)
		) as ModelFieldStateImageVisibility;
	}
}
